/**
 * SketchPath - Base Path Class
 * Ported from cc.sketchchair.sketch.SketchPath.java
 *
 * Contains a list of SketchPoints that can be connected with
 * linear segments or Bezier curves.
 */

import { SketchShape } from './SketchShape.js';
import { SketchPoint } from './SketchPoint.js';
import { Vec2D } from './geometry/Vec2D.js';
import { bezierPoint, bezierTangent, bezierLength } from './geometry/BezierUtils.js';

export class SketchPath extends SketchShape {
    // Offset type constants
    static OFFSET_LEFT = 0;
    static OFFSET_RIGHT = 1;
    static OFFSET_BOTH = 2;

    // Bezier detail (steps per curve for calculations)
    static BEZIER_DETAIL = 0.05; // 20 steps per curve

    /**
     * Create a new sketch path
     * @param {Object} parentSketch - Parent sketch
     * @param {Object|Array} [data] - JSON data or array of points
     */
    constructor(parentSketch = null, data = null) {
        super(parentSketch);

        this.setType(SketchShape.TYPE_PATH);

        // Point list
        this.points = [];

        // Interior hole rings (Array<Array<SketchPoint>>). Set when the
        // outline legitimately encloses holes - e.g. a closed brush stroke
        // (drawn ring) whose offset outline is an annulus. Consumers
        // (mesh extrusion, SVG/DXF export, rendering) subtract these from
        // the filled area.
        this.holes = [];

        // Optimization
        this.isOptimized = false;
        this.pointDist = 5; // Distance between optimized points

        // Caching
        this.cacheLength = false;
        this.cachedLength = -1;

        // State
        this.isOutline = false;
        this.offsetType = SketchPath.OFFSET_BOTH;

        // Load from data
        if (data) {
            if (Array.isArray(data)) {
                // Array of points
                data.forEach(p => this.add(p));
            } else if (data.points) {
                // JSON object
                this.fromJSON(data);
            }
        }
    }

    // ============================================
    // Point Management
    // ============================================

    /**
     * Add a point to the path
     * @param {SketchPoint|Vec2D|number} x - Point, Vec2D, or X coordinate
     * @param {number} [y] - Y coordinate
     */
    add(x, y) {
        let point;

        if (x instanceof SketchPoint) {
            point = x;
        } else if (x instanceof Vec2D) {
            point = new SketchPoint(x);
        } else {
            point = new SketchPoint(x, y);
        }

        this.points.push(point);
        this.resetCachedVariables();
    }

    /**
     * Insert point at index
     * @param {number} index - Index to insert at
     * @param {SketchPoint} point - Point to insert
     */
    insert(index, point) {
        this.points.splice(index, 0, point);
        this.resetCachedVariables();
    }

    /**
     * Remove point
     * @param {SketchPoint|number} point - Point or index to remove
     */
    remove(point) {
        if (typeof point === 'number') {
            this.points.splice(point, 1);
        } else {
            const index = this.points.indexOf(point);
            if (index !== -1) {
                this.points.splice(index, 1);
            }
        }
        this.resetCachedVariables();
    }

    /**
     * Get point at index
     * @param {number} i - Index
     * @returns {SketchPoint} Point
     */
    get(i) {
        return this.points[i];
    }

    /**
     * Get last point
     * @returns {SketchPoint} Last point
     */
    getLast() {
        return this.points[this.points.length - 1];
    }

    /**
     * Get number of points
     * @returns {number} Size
     */
    size() {
        return this.points.length;
    }

    /**
     * Get number of points (alias)
     * @returns {number} Length
     */
    getLength() {
        return this.points.length;
    }

    // ============================================
    // Path Geometry
    // ============================================

    /**
     * Get position at percent along path
     * @param {number} percent - Percent (0-1)
     * @returns {Vec2D|null} Position
     */
    getPos(percent) {
        if (percent > 1 || percent < 0 || this.points.length < 1) {
            return null;
        }

        const totalLen = this.getPathLength();
        const destLen = totalLen * percent;

        let length = 0;
        const offset = this.closed ? 0 : 1;

        let lastMeasuredPoint = new Vec2D(this.points[0]);

        for (let i = 0; i < this.points.length - offset; i++) {
            const curP = this.points[i];
            const nextP = i < this.points.length - 1 ? this.points[i + 1] : this.points[0];

            if (curP.containsBezier() || nextP.containsBezier()) {
                // Bezier segment. INTERPOLATE WITHIN each sample step (like
                // the linear branch below): returning the raw sample made
                // getPos a ~17px step function along beziers, so the finite
                // differences in getTangentAt collapsed to a ZERO tangent -
                // rib planes spawned on bezier spans got a degenerate
                // normal, found no crossings and fell back to 600px slabs.
                // Integer-step loop so t reaches exactly 1 (the float
                // accumulation never did) - keep getPathLength identical.
                const bez1 = curP.getControlPoint2();
                const bez2 = nextP.getControlPoint1();
                const steps = Math.max(1, Math.round(1 / SketchPath.BEZIER_DETAIL));

                for (let k = 1; k <= steps; k++) {
                    const t = k / steps;
                    const x = bezierPoint(curP.x, bez1.x, bez2.x, nextP.x, t);
                    const y = bezierPoint(curP.y, bez1.y, bez2.y, nextP.y, t);

                    const measuredPoint = new Vec2D(x, y);
                    const stepLen = lastMeasuredPoint.distanceTo(measuredPoint);

                    if (length + stepLen >= destLen) {
                        const f = stepLen > 1e-9 ? (destLen - length) / stepLen : 0;
                        return new Vec2D(
                            lastMeasuredPoint.x + (measuredPoint.x - lastMeasuredPoint.x) * f,
                            lastMeasuredPoint.y + (measuredPoint.y - lastMeasuredPoint.y) * f
                        );
                    }

                    length += stepLen;
                    lastMeasuredPoint = measuredPoint;
                }
            } else {
                // Linear segment
                const segLen = curP.distanceTo(nextP);
                length += segLen;

                if (length >= destLen) {
                    // Interpolate along segment
                    const lastLen = length - segLen;
                    const curPos = destLen - lastLen;
                    const t = curPos / segLen;

                    const x = curP.x + (nextP.x - curP.x) * t;
                    const y = curP.y + (nextP.y - curP.y) * t;

                    return new Vec2D(x, y);
                }

                lastMeasuredPoint = new Vec2D(nextP);
            }
        }

        return null;
    }

    /**
     * Get tangent vector at percent along path
     *
     * Uses finite difference method (±0.1% offset) to calculate the tangent
     * direction at any point along the path. This works for both straight
     * segments and bezier curves.
     *
     * Note: This is the actual tangent (direction of path). For the normal
     * (perpendicular), use getPerpendicular() which rotates this 90°.
     *
     * Algorithm from SketchPath.java getPerpendicular() - misleadingly named!
     * The Java method returns tangent, not perpendicular.
     *
     * @param {number} percent - Percent (0-1)
     * @returns {Vec2D|null} Tangent vector (normalized)
     */
    getTangentAt(percent) {
        // Finite differences; widen the offset if the two samples coincide
        // (defence in depth - sparse/degenerate paths must never produce a
        // ZERO tangent, which becomes a degenerate slice plane normal)
        for (const delta of [0.001, 0.005, 0.02]) {
            const p1 = this.getPos(Math.max(0, percent - delta));
            const p2 = this.getPos(Math.min(1, percent + delta));
            if (!p1 || !p2) continue;

            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            if (dx * dx + dy * dy > 1e-12) {
                // Tangent = direction of path (from p1 to p2). Winding-order
                // flip (Java isClockwise()) is handled at a higher level.
                return new Vec2D(dx, dy).normalize();
            }
        }
        return null;
    }

    /**
     * Get perpendicular (normal) vector at percent along path
     *
     * This is the normal vector perpendicular to the path direction,
     * which is used for slice plane normals.
     *
     * Implementation: Calculate tangent, then rotate 90° counter-clockwise.
     *
     * @param {number} percent - Percent (0-1)
     * @returns {Vec2D|null} Perpendicular vector (normalized)
     */
    getPerpendicular(percent) {
        const tangent = this.getTangentAt(percent);
        if (!tangent) return null;

        // Perpendicular is 90 degrees counter-clockwise rotation
        // Rotation matrix for 90° CCW: (x, y) -> (-y, x)
        return new Vec2D(-tangent.y, tangent.x);
    }

    /**
     * Get normal vector at percent along path (alias for getPerpendicular)
     *
     * For clarity when used in slicing context where "normal" is more
     * intuitive than "perpendicular".
     *
     * @param {number} percent - Percent (0-1)
     * @returns {Vec2D|null} Normal vector (normalized)
     */
    getNormalAt(percent) {
        return this.getPerpendicular(percent);
    }

    /**
     * Calculate total path length
     * @returns {number} Length
     */
    getPathLength() {
        if (this.cachedLength !== -1 && this.cacheLength) {
            return this.cachedLength;
        }

        let length = 0;
        const offset = this.closed ? 0 : 1;

        let lastMeasured = null;

        for (let i = 0; i < this.points.length - offset; i++) {
            const curP = this.points[i];
            const nextP = i < this.points.length - 1 ? this.points[i + 1] : this.points[0];

            if (curP.containsBezier() || nextP.containsBezier()) {
                // Bezier segment - approximate length
                const bez1 = curP.getControlPoint2();
                const bez2 = nextP.getControlPoint1();

                lastMeasured = new Vec2D(curP);

                const steps = Math.max(1, Math.round(1 / SketchPath.BEZIER_DETAIL));
                for (let k = 1; k <= steps; k++) {
                    const t = k / steps;
                    const x = bezierPoint(curP.x, bez1.x, bez2.x, nextP.x, t);
                    const y = bezierPoint(curP.y, bez1.y, bez2.y, nextP.y, t);

                    const measuredPoint = new Vec2D(x, y);
                    length += lastMeasured.distanceTo(measuredPoint);
                    lastMeasured = measuredPoint;
                }
            } else {
                // Linear segment
                length += curP.distanceTo(nextP);
            }
        }

        if (this.cacheLength) {
            this.cachedLength = length;
        }

        return length;
    }

    /**
     * Get bounding box
     * @returns {Object} {minX, minY, maxX, maxY}
     */
    getBounds() {
        if (this.points.length === 0) {
            return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
        }

        let minX = this.points[0].x;
        let minY = this.points[0].y;
        let maxX = this.points[0].x;
        let maxY = this.points[0].y;

        for (let i = 1; i < this.points.length; i++) {
            const p = this.points[i];
            minX = Math.min(minX, p.x);
            minY = Math.min(minY, p.y);
            maxX = Math.max(maxX, p.x);
            maxY = Math.max(maxY, p.y);

            // Also check control points if Bezier
            if (p.controlPoint1) {
                minX = Math.min(minX, p.controlPoint1.x);
                minY = Math.min(minY, p.controlPoint1.y);
                maxX = Math.max(maxX, p.controlPoint1.x);
                maxY = Math.max(maxY, p.controlPoint1.y);
            }
            if (p.controlPoint2) {
                minX = Math.min(minX, p.controlPoint2.x);
                minY = Math.min(minY, p.controlPoint2.y);
                maxX = Math.max(maxX, p.controlPoint2.x);
                maxY = Math.max(maxY, p.controlPoint2.y);
            }
        }

        return { minX, minY, maxX, maxY };
    }

    // ============================================
    // Rendering
    // ============================================

    /**
     * Render path to canvas
     * @param {CanvasRenderingContext2D} ctx - Canvas context
     */
    render(ctx) {
        if (this.points.length < 2) return;

        ctx.beginPath();
        ctx.moveTo(this.points[0].x, this.points[0].y);

        for (let i = 0; i < this.points.length - 1; i++) {
            const curP = this.points[i];
            const nextP = this.points[i + 1];

            if (curP.containsBezier() || nextP.containsBezier()) {
                // Bezier curve
                const cp1 = curP.getControlPoint2();
                const cp2 = nextP.getControlPoint1();
                ctx.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, nextP.x, nextP.y);
            } else {
                // Linear segment
                ctx.lineTo(nextP.x, nextP.y);
            }
        }

        if (this.closed && this.points.length > 2) {
            const lastP = this.points[this.points.length - 1];
            const firstP = this.points[0];

            if (lastP.containsBezier() || firstP.containsBezier()) {
                const cp1 = lastP.getControlPoint2();
                const cp2 = firstP.getControlPoint1();
                ctx.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, firstP.x, firstP.y);
            } else {
                ctx.lineTo(firstP.x, firstP.y);
            }
            ctx.closePath();
        }

        // Hole rings as extra subpaths (stroked and excluded from the fill)
        for (const hole of this.holes || []) {
            for (let i = 0; i < hole.length; i++) {
                const p = hole[i];
                if (i === 0) ctx.moveTo(p.x, p.y);
                else ctx.lineTo(p.x, p.y);
            }
            ctx.closePath();
        }

        // Apply style based on state
        if (this.highlighted) {
            ctx.strokeStyle = '#4CAF50';
            ctx.lineWidth = 3;
        } else if (this.selected) {
            ctx.strokeStyle = '#2196F3';
            ctx.lineWidth = 2;
        } else {
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 1;
        }

        ctx.stroke();

        // Fill if closed (evenodd so holes stay empty)
        if (this.closed) {
            ctx.fillStyle = 'rgba(200, 200, 200, 0.2)';
            ctx.fill('evenodd');
        }
    }

    // ============================================
    // Utility
    // ============================================

    /**
     * Build/rebuild path
     */
    build() {
        this.resetCachedVariables();
    }

    /**
     * Reset cached variables
     */
    resetCachedVariables() {
        this.cachedLength = -1;
    }

    /**
     * Clone path
     * @returns {SketchPath} Cloned path
     */
    clone() {
        const newPath = new SketchPath(this.parentSketch);
        newPath.setClosed(this.closed);

        for (let i = 0; i < this.points.length; i++) {
            newPath.add(this.points[i].clone());
        }

        newPath.holes = (this.holes || []).map(hole => hole.map(p => p.clone()));
        newPath.strokeId = this.strokeId; // Stable linked-stroke identity

        return newPath;
    }

    /**
     * Copy path with new parent
     * @param {Object} parentSketch - New parent
     * @returns {SketchPath} Copied path
     */
    copy(parentSketch) {
        const newPath = this.clone();
        newPath.setParentSketch(parentSketch);
        return newPath;
    }

    /**
     * Get path representation (returns self)
     * @returns {SketchPath} This path
     */
    getPath() {
        return this;
    }

    /**
     * Check if point is inside closed path
     * @param {Vec2D} point - Point to check
     * @returns {boolean} True if inside
     */
    isPointInside(point) {
        if (!this.closed || this.points.length < 3) {
            return false;
        }

        // Ray casting algorithm
        let inside = false;
        const x = point.x;
        const y = point.y;

        for (let i = 0, j = this.points.length - 1; i < this.points.length; j = i++) {
            const xi = this.points[i].x;
            const yi = this.points[i].y;
            const xj = this.points[j].x;
            const yj = this.points[j].y;

            const intersect = ((yi > y) !== (yj > y)) &&
                (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

            if (intersect) inside = !inside;
        }

        return inside;
    }

    /**
     * Destroy path
     */
    destroy() {
        this.points = [];
        this.setDestroy(true);
    }

    // ============================================
    // Serialization
    // ============================================

    /**
     * Convert to JSON
     * @returns {Object} JSON representation
     */
    toJSON() {
        const json = {
            type: 'SketchPath',
            id: this.id,
            strokeId: this.strokeId,
            closed: this.closed,
            union: this.union,
            isConstructionLine: this.isConstructionLine,
            points: this.points.map(p => p.toJSON())
        };
        if (this.holes && this.holes.length > 0) {
            json.holes = this.holes.map(hole => hole.map(p => p.toJSON()));
        }
        return json;
    }

    /**
     * Load from JSON
     * @param {Object} data - JSON data
     */
    fromJSON(data) {
        if (data.id !== undefined) {
            this.setId(data.id);
        }
        if (data.strokeId !== undefined) {
            this.strokeId = data.strokeId;
        }
        if (data.closed !== undefined) {
            this.setClosed(data.closed);
        }
        if (data.union !== undefined) {
            this.union = data.union;
        }
        if (data.isConstructionLine !== undefined) {
            this.setIsConstructionLine(data.isConstructionLine);
        }
        if (data.points) {
            data.points.forEach(pointData => {
                this.add(SketchPoint.fromJSON(pointData));
            });
        }
        if (data.holes) {
            this.holes = data.holes.map(hole =>
                hole.map(pointData => SketchPoint.fromJSON(pointData)));
        }
    }

    /**
     * Create from JSON
     * @param {Object} parentSketch - Parent sketch
     * @param {Object} data - JSON data
     * @returns {SketchPath} New path
     */
    static fromJSON(parentSketch, data) {
        const path = new SketchPath(parentSketch);
        path.fromJSON(data);
        return path;
    }

    // ============================================
    // Unimplemented abstract methods
    // ============================================

    getClosestPercent(x, y) {
        // TODO: Implement if needed
        return 0;
    }

    getClosestPoint(point) {
        // TODO: Implement if needed
        return this.points[0];
    }

    insertPoint(point) {
        this.add(point);
    }

    removeVertex(v) {
        this.remove(v);
    }

    select() {
        this.selected = true;
    }

    unselect() {
        this.selected = false;
    }

    selectNodes(x, y) {
        // TODO: Implement node selection
    }

    mouseDragged(x, y) {
        // TODO: Implement if needed
    }

    mouseReleased(x, y) {
        // TODO: Implement if needed
    }

    movePoint(selectedVec, planePoint) {
        // TODO: Implement if needed
    }

    offset() {
        // TODO: Implement offset calculation
    }

    optimize() {
        // TODO: Implement path optimization
    }

    flipHorizontal(centre) {
        // TODO: Implement flip
    }

    scale(scaleFactor, centre) {
        // TODO: Implement scale
    }

    replace(obj) {
        // TODO: Implement replace
    }

    renderSilhouette(ctx) {
        this.render(ctx);
    }

    renderPickBuffer(ctx) {
        // TODO: Implement pick buffer if needed
    }

    getSketchPointpickBuffer(col) {
        return null;
    }

    getVec2DpickBuffer(col) {
        return null;
    }

    setVec2DpickBuffer(col, selectedVec, selectedShape, selectedVecPlane, isSelectedVecOnOutline) {
        return null;
    }

    getClosestPointAlongPath(x, y) {
        return this.points[0];
    }

    getOverShape(x, y) {
        return null;
    }

    getOutlineGeneralPath() {
        return null;
    }
}
